import { Button, TextField, Divider, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getLanguage } from 'data/locale'
import { useTitle } from 'hooks'
import { trackPageView } from 'utils/mixpanel'

import { changePassword, changeUsername, logoutUser, redirectTo } from 'store/actions/user'
import { selectHasExecutePermissions, selectUser } from 'store/selectors/user'

const UserProfilePage = () => {
  const { t } = getLanguage()
  const dispatch = useDispatch()

  const hasExecutePermissions = useSelector(selectHasExecutePermissions)
  const { username } = useSelector(selectUser)

  const [usernameState, setUsernameState] = useState({
    username: '',
    password: '',
    isValid: false,
    errors: {
      username: '',
      password: '',
      other: ''
    }
  })

  const [passwordState, setPasswordState] = useState({
    password: '',
    newPassword: '',
    repeatPassword: '',
    isValid: false,
    errors: {
      password: '',
      newPassword: '',
      repeatPassword: '',
      other: ''
    }
  })

  useTitle(t => t.user.profileEdit.changeLogin)

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    if (!hasExecutePermissions) {
      dispatch(redirectTo('/search'))
    }
  }, [hasExecutePermissions])

  const handleNewUsernameChange = e => {
    validateUsernameState(e.target.value, usernameState.password)
    setUsernameState(state => ({ ...state, username: e.target.value }))
  }

  const handleNewUsernamePasswordChange = e => {
    validateUsernameState(usernameState.username, e.target.value)
    setUsernameState(state => ({ ...state, password: e.target.value }))
  }

  const handleCurrentPasswordChange = e => {
    validatePasswordState(e.target.value, passwordState.newPassword, passwordState.repeatPassword)
    setPasswordState(state => ({ ...state, password: e.target.value }))
  }

  const handleNewPasswordChange = e => {
    validatePasswordState(passwordState.password, e.target.value, passwordState.repeatPassword)
    setPasswordState(state => ({ ...state, newPassword: e.target.value }))
  }

  const handleRepeatPasswordChange = e => {
    validatePasswordState(passwordState.password, passwordState.newPassword, e.target.value)
    setPasswordState(state => ({ ...state, repeatPassword: e.target.value }))
  }

  const validateUsernameState = (newUsername, currentPassword) => {
    const empty = ''
    const errors = []

    if (newUsername === empty) {
      errors.push({ username: t.user.fieldRequired })
    } else if (newUsername === username) {
      errors.push({ username: t.user.profileEdit.newUsernameError })
    }
    if (currentPassword === empty) {
      errors.push({ password: t.user.fieldRequired })
    }

    setUsernameState(state => ({
      ...state,
      errors: Object.assign({}, ...errors),
      isValid: errors.length === 0
    }))
  }

  const validatePasswordState = (current, newPassword, repeat) => {
    const empty = ''
    const errors = []

    if (current === empty) {
      errors.push({ password: t.user.fieldRequired })
    }
    if (newPassword === empty) {
      errors.push({ newPassword: t.user.fieldRequired })
    } else if (newPassword === current) {
      errors.push({ newPassword: t.user.profileEdit.newPasswordError })
    } else if (newPassword !== repeat) {
      errors.push({ newPassword: t.user.updatePasswordError })
    }
    if (repeat === empty) {
      errors.push({ repeatPassword: t.user.fieldRequired })
    }

    setPasswordState(state => ({
      ...state,
      errors: Object.assign({}, ...errors),
      isValid: errors.length === 0
    }))
  }

  const submitNewUsername = async () => {
    const result = await dispatch(changeUsername(usernameState.password, usernameState.username))
    if (result.success) {
      dispatch(logoutUser())
    } else {
      setUsernameState(state => ({ ...state, errors: result.errors }))
    }
  }

  const submitNewPassword = async () => {
    const result = await dispatch(changePassword(passwordState.password, passwordState.newPassword))
    if (result.success) {
      dispatch(logoutUser())
    } else {
      setPasswordState(state => ({ ...state, errors: result.errors }))
    }
  }

  return (
    <div className="main user-edit">
      <Grid container>
        <Grid size={{ xs: 12, md: 9 }}>
          <form>
            <h2> {t.user.profileEdit.editUsername} </h2>
            <TextField
              id="username"
              label={t.user.profileEdit.actualUsername}
              value={username}
              variant="standard"
              autoComplete="off"
            />
            <TextField
              id="newusername"
              label={t.user.profileEdit.newUsername}
              onChange={handleNewUsernameChange}
              error={!!usernameState.errors.username}
              helperText={usernameState.errors.username}
              variant="standard"
            />
            <TextField
              id="currentpassword"
              type="password"
              label={t.user.profileEdit.actualPassword}
              onChange={handleNewUsernamePasswordChange}
              error={!!usernameState.errors.password}
              helperText={usernameState.errors.password}
              variant="standard"
            />
            <Button disabled={!usernameState.isValid} onClick={submitNewUsername}>
              {t.user.profileEdit.submitUsername}
            </Button>
            {usernameState.errors.other && (
              <p className="message-danger">{usernameState.errors.other}</p>
            )}
          </form>
          <Divider />
          <form>
            <h2> {t.user.profileEdit.editPassword} </h2>
            <TextField
              id="actualpassword"
              type="password"
              label={t.user.profileEdit.actualPassword}
              onChange={handleCurrentPasswordChange}
              error={!!passwordState.errors.password}
              helperText={passwordState.errors.password}
              variant="standard"
            />
            <TextField
              id="newpassword"
              type="password"
              label={t.user.updatePassword}
              onChange={handleNewPasswordChange}
              error={!!passwordState.errors.newPassword}
              helperText={passwordState.errors.newPassword}
              variant="standard"
            />
            <TextField
              id="newpasswordrepeat"
              type="password"
              label={t.user.updatePasswordRepeat}
              onChange={handleRepeatPasswordChange}
              error={!!passwordState.errors.repeatPassword}
              helperText={passwordState.errors.repeatPassword}
              variant="standard"
            />
            <Button disabled={!passwordState.isValid} onClick={submitNewPassword}>
              {t.user.profileEdit.submitPassword}
            </Button>
            {passwordState.errors.other && (
              <p className="message-danger">{passwordState.errors.other}</p>
            )}
          </form>
        </Grid>
      </Grid>
    </div>
  )
}

export default UserProfilePage
